import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Utensils, ClipboardList, Leaf, ArrowRight, Info } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
export function ReportConcernPage() {
  return <div className="min-h-screen flex flex-col w-full bg-gov-light" data-id="element-503">
      <Header data-id="element-504" />

      <main className="flex-grow" data-id="element-505">
        {/* Page Header */}
        <div className="bg-gov-navy text-white py-8 md:py-16" data-id="element-506">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-507">
            <div className="flex items-center space-x-3 mb-4" data-id="element-508">
              <div className="bg-gov-amber/20 p-2 rounded-lg" data-id="element-509">
                <AlertTriangle className="w-6 h-6 text-gov-amber" data-id="element-510" />
              </div>
              <span className="text-gov-amber font-semibold tracking-wider uppercase text-sm" data-id="element-511">
                Public Health Services
              </span>
            </div>
            <h1 className="text-3xl md:text-5xl font-black mb-4" data-id="element-512">
              Report a Concern
            </h1>
            <p className="text-lg md:text-xl text-gray-300 max-w-3xl leading-relaxed" data-id="element-513">
              Help us keep Franklin County safe. Select the type of concern
              below to ensure your report is routed to the correct department
              for immediate review.
            </p>
          </div>
        </div>

        {/* Triage Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-24" data-id="element-514">
          {/* Emergency Notice */}
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-12 rounded-r-lg flex items-start shadow-sm max-w-4xl mx-auto" data-id="element-515">
            <Info className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-0.5" data-id="element-516" />
            <div data-id="element-517">
              <h3 className="text-blue-800 font-bold mb-1" data-id="element-518">
                Is this a medical emergency?
              </h3>
              <p className="text-blue-700 text-sm" data-id="element-519">
                If you or someone else is experiencing a life-threatening
                medical emergency, please call{' '}
                <strong className="font-black" data-id="element-520">911</strong> immediately. Do not
                use this form for emergencies.
              </p>
            </div>
          </div>

          {/* Selection Cards — 3 columns */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-521">
            {/* Card 1: Foodborne Illness */}
            <motion.a href="#report-food" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4,
            delay: 0.1
          }} className="group relative bg-white rounded-2xl p-8 shadow-sm border border-gov-border hover:shadow-xl hover:border-gov-amber/50 transition-all duration-300 flex flex-col h-full" data-id="element-522">
              <div className="absolute top-0 left-0 w-full h-1.5 bg-gov-navy rounded-t-2xl group-hover:bg-gov-amber transition-colors duration-300" data-id="element-523"></div>
              <div className="w-14 h-14 bg-gov-navy/5 text-gov-navy rounded-xl flex items-center justify-center mb-5 group-hover:bg-gov-navy group-hover:text-white transition-colors duration-300" data-id="element-524">
                <AlertTriangle className="w-7 h-7" data-id="element-525" />
              </div>
              <h2 className="text-xl font-bold text-gov-navy mb-3 group-hover:text-gov-amber transition-colors" data-id="element-526">
                Foodborne Illness
              </h2>
              <p className="text-gov-slate mb-6 flex-grow leading-relaxed" data-id="element-527">
                Report a suspected food-related illness. Use this form if you or
                someone in your household became sick after eating at a food
                establishment in Franklin County.
              </p>
              <div className="flex items-center text-gov-navy font-bold text-sm mt-auto group-hover:text-gov-amber transition-colors" data-id="element-528">
                Report an Illness{' '}
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform" data-id="element-529" />
              </div>
            </motion.a>

            {/* Card 2: Restaurant / Food Safety Complaint */}
            <motion.a href="#report-food-complaint" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4,
            delay: 0.2
          }} className="group relative bg-white rounded-2xl p-8 shadow-sm border border-gov-border hover:shadow-xl hover:border-gov-amber/50 transition-all duration-300 flex flex-col h-full" data-id="element-530">
              <div className="absolute top-0 left-0 w-full h-1.5 bg-gov-navy rounded-t-2xl group-hover:bg-gov-amber transition-colors duration-300" data-id="element-531"></div>
              <div className="w-14 h-14 bg-gov-navy/5 text-gov-navy rounded-xl flex items-center justify-center mb-5 group-hover:bg-gov-navy group-hover:text-white transition-colors duration-300" data-id="element-532">
                <ClipboardList className="w-7 h-7" data-id="element-533" />
              </div>
              <h2 className="text-xl font-bold text-gov-navy mb-3 group-hover:text-gov-amber transition-colors" data-id="element-534">
                Restaurant or Food Safety Complaint
              </h2>
              <p className="text-gov-slate mb-6 flex-grow leading-relaxed" data-id="element-535">
                Report unsanitary conditions, improper food handling,
                temperature issues, pests, or other food safety concerns at a
                restaurant, grocery store, or food truck in Franklin County. Use
                this form if no one became ill.
              </p>
              <div className="flex items-center text-gov-navy font-bold text-sm mt-auto group-hover:text-gov-amber transition-colors" data-id="element-536">
                File a Complaint{' '}
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform" data-id="element-537" />
              </div>
            </motion.a>

            {/* Card 3: Environmental Health */}
            <motion.a href="#report-environmental" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4,
            delay: 0.3
          }} className="group relative bg-white rounded-2xl p-8 shadow-sm border border-gov-border hover:shadow-xl hover:border-gov-amber/50 transition-all duration-300 flex flex-col h-full" data-id="element-538">
              <div className="absolute top-0 left-0 w-full h-1.5 bg-gov-navy rounded-t-2xl group-hover:bg-gov-amber transition-colors duration-300" data-id="element-539"></div>
              <div className="w-14 h-14 bg-gov-navy/5 text-gov-navy rounded-xl flex items-center justify-center mb-5 group-hover:bg-gov-navy group-hover:text-white transition-colors duration-300" data-id="element-540">
                <Leaf className="w-7 h-7" data-id="element-541" />
              </div>
              <h2 className="text-xl font-bold text-gov-navy mb-3 group-hover:text-gov-amber transition-colors" data-id="element-542">
                Environmental Health Concern
              </h2>
              <p className="text-gov-slate mb-6 flex-grow leading-relaxed" data-id="element-543">
                Report concerns regarding public pools, spas, lodging
                facilities, water quality, mold, bed bugs, sewage leaks, or
                other environmental health hazards.
              </p>
              <div className="flex items-center text-gov-navy font-bold text-sm mt-auto group-hover:text-gov-amber transition-colors" data-id="element-544">
                Start Environmental Report{' '}
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform" data-id="element-545" />
              </div>
            </motion.a>
          </div>

          {/* Alternative Contact */}
          <div className="mt-16 text-center max-w-2xl mx-auto" data-id="element-546">
            <p className="text-gov-slate" data-id="element-547">
              Not sure which option to choose? You can also report concerns by
              calling our main office at{' '}
              <strong className="text-gov-navy font-semibold" data-id="element-548">
                (555) 123-4567
              </strong>{' '}
              during regular business hours.
            </p>
          </div>
        </div>
      </main>

      <Footer data-id="element-549" />
    </div>;
}